import React from "react";

/**
 * Barre de progression stylée 2026.
 *
 * - Gradient primary → secondary
 * - Animation : fill au mount (transition width), shimmer subtil au-dessus,
 *   pulse soft sur le bord droit pour évoquer un don encore actif
 * - Pourcentage affiché à droite (couleur ink)
 *
 * Props :
 *   percent : 0-100 (number)
 *   showLabel : boolean (default true) — affiche le % à droite
 *   size : "sm" | "md" (default "md")
 */
const styles = `
.t26-progress {
  display: flex; align-items: center; gap: 12px;
  width: 100%;
}
.t26-progress__track {
  flex: 1;
  position: relative;
  height: 10px;
  background: color-mix(in oklab, var(--primary) 10%, var(--bg-2));
  border-radius: var(--r-pill);
  overflow: hidden;
  isolation: isolate;
}
.t26-progress__track--sm { height: 6px; }

.t26-progress__fill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: var(--r-pill);
  background: linear-gradient(90deg,
    var(--primary) 0%,
    color-mix(in oklab, var(--primary) 60%, var(--secondary)) 50%,
    var(--secondary) 100%);
  transition: width 1.1s cubic-bezier(.16, 1, .3, 1);
  box-shadow: 0 0 0 1px color-mix(in oklab, var(--primary) 30%, transparent) inset;
}
[dir="rtl"] .t26-progress__fill {
  inset: 0 0 0 auto;
}

/* Shimmer animé qui traverse la barre — discret, juste un reflet */
.t26-progress__fill::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255,255,255,.22) 50%,
    transparent 100%);
  background-size: 200% 100%;
  animation: t26-progress-shimmer 2.4s linear infinite;
  border-radius: inherit;
}
@keyframes t26-progress-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Bord droit lumineux pour évoquer "actif" */
.t26-progress__fill::after {
  content: "";
  position: absolute;
  top: -3px; bottom: -3px;
  right: -3px;
  width: 8px;
  border-radius: 50%;
  background: var(--secondary);
  box-shadow: 0 0 12px 2px color-mix(in oklab, var(--secondary) 60%, transparent);
  animation: t26-progress-pulse 1.8s ease-in-out infinite;
  opacity: .9;
}
[dir="rtl"] .t26-progress__fill::after {
  right: auto;
  left: -3px;
}
@keyframes t26-progress-pulse {
  0%, 100% { transform: scale(1); opacity: .9; }
  50% { transform: scale(1.25); opacity: 1; }
}

/* Quand la barre est pleine (≥98%), on cache le glow pour pas qu'il déborde */
.t26-progress__fill[data-complete="true"]::after {
  display: none;
}

.t26-progress__label {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
  min-width: 3em;
  text-align: end;
}
`;

export default function Progress({ percent = 0, showLabel = true, size = "md" }) {
    const safePercent = Math.max(0, Math.min(100, Number(percent) || 0));
    const complete = safePercent >= 98;
    const sizeClass = size === "sm" ? "t26-progress__track--sm" : "";

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="t26-progress" role="progressbar" aria-valuenow={Math.round(safePercent)} aria-valuemin={0} aria-valuemax={100}>
                <div className={`t26-progress__track ${sizeClass}`}>
                    <div
                        className="t26-progress__fill"
                        style={{ width: `${safePercent}%` }}
                        data-complete={complete ? "true" : "false"}
                    />
                </div>
                {showLabel && (
                    <span className="t26-progress__label">{Math.round(safePercent)}%</span>
                )}
            </div>
        </>
    );
}
